import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FileText,
  ArrowLeft,
  Share2,
  PlusCircle,
  Search,
  BookmarkPlus,
  X,
  Bookmark,
  Send,
  Globe,
  FolderOpen,
  Trash2,
  Loader2,
} from 'lucide-react';
import { useSavedSearches } from '@/hooks/useSavedSearches';
import { encodeFiltersToParams } from '@/utils/searchParamsEncoding';

interface SidebarContextualActionsProps {
  isCollapsed: boolean;
  onMobileClose?: () => void;
}

type PageContext =
  | 'profile-overview'
  | 'profile-subsidies'
  | 'subsidy-search'
  | 'subsidy-detail'
  | 'none';

/**
 * Determines the current page context based on the URL path
 */
function getPageContext(pathname: string): PageContext {
  // Profile subsidies tab: /profil/:id/subventions
  if (/^\/profil\/[^/]+\/subventions/.test(pathname)) {
    return 'profile-subsidies';
  }

  // Profile overview: /profil/:id (without additional tabs, or with non-subventions tab)
  if (/^\/profil\/[^/]+$/.test(pathname) || /^\/profil\/[^/]+\/(?!subventions)/.test(pathname)) {
    return 'profile-overview';
  }

  // Subsidy search: /subventions/recherche
  if (pathname === '/subventions/recherche') {
    return 'subsidy-search';
  }

  // Subsidy detail: /subventions/:id (but not /recherche)
  if (/^\/subventions\/[^/]+$/.test(pathname) && pathname !== '/subventions/recherche') {
    return 'subsidy-detail';
  }

  return 'none';
}

/**
 * Extract profile ID from URL path
 */
function extractProfileId(pathname: string): string | null {
  const match = pathname.match(/^\/profil\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Extract subsidy ID from URL path
 */
function extractSubsidyId(pathname: string): string | null {
  const match = pathname.match(/^\/subventions\/([^/]+)$/);
  return match && match[1] !== 'recherche' ? match[1] : null;
}

export function SidebarContextualActions({ isCollapsed, onMobileClose }: SidebarContextualActionsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const pageContext = getPageContext(location.pathname);

  // Saved searches hook (only load on subsidy-search page)
  const { savedSearches, isLoading: savedSearchesLoading, deleteSearch, recordUse, isDeleting } = useSavedSearches();

  // State for subsidy counts (updated via custom events from the subsidies page)
  const [subsidyCounts, setSubsidyCounts] = useState({ rejected: 0, saved: 0 });

  // Listen for count updates from the subsidies page
  useEffect(() => {
    const handleCountUpdate = (event: CustomEvent<{ rejected: number; saved: number }>) => {
      setSubsidyCounts(event.detail);
    };

    window.addEventListener('sidebar-subsidy-counts-update', handleCountUpdate as EventListener);
    return () => {
      window.removeEventListener('sidebar-subsidy-counts-update', handleCountUpdate as EventListener);
    };
  }, []);

  // Don't render anything if no context
  if (pageContext === 'none') {
    return null;
  }

  const profileId = extractProfileId(location.pathname);
  const subsidyId = extractSubsidyId(location.pathname);

  const handleAction = (action: () => void) => {
    action();
    onMobileClose?.();
  };

  // Common button styles - min-height instead of fixed height to allow text wrapping
  const buttonClass = cn(
    'w-full justify-start items-start gap-3 min-h-9 py-2 px-3 text-sm font-medium whitespace-normal',
    'text-foreground hover:text-primary hover:bg-secondary',
    isCollapsed && 'justify-center items-center px-0'
  );

  const renderActions = () => {
    switch (pageContext) {
      case 'profile-overview':
        return (
          <>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => navigate(`/profil/${profileId}/subventions`))}
            >
              <Search className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Voir les subventions</span>}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                window.dispatchEvent(new CustomEvent('sidebar-action-enrich-website', {
                  detail: { profileId }
                }));
              })}
            >
              <Globe className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Enrichissement par Site web</span>}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                window.dispatchEvent(new CustomEvent('sidebar-action-enrich-documents', {
                  detail: { profileId }
                }));
              })}
            >
              <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Enrichissement par documents</span>}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                window.dispatchEvent(new CustomEvent('sidebar-action-client-projects', {
                  detail: { profileId }
                }));
              })}
            >
              <FolderOpen className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Projets du Client</span>}
            </Button>
          </>
        );

      case 'profile-subsidies':
        return (
          <>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                window.dispatchEvent(new CustomEvent('sidebar-action-filter-rejected'));
              })}
            >
              <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && (
                <span className="flex items-center gap-2 text-left break-words">
                  Rejetées
                  <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full flex-shrink-0">
                    {subsidyCounts.rejected}
                  </span>
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                window.dispatchEvent(new CustomEvent('sidebar-action-filter-saved'));
              })}
            >
              <Bookmark className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && (
                <span className="flex items-center gap-2 text-left break-words flex-wrap">
                  Sauvegardées
                  <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full flex-shrink-0">
                    {subsidyCounts.saved}
                  </span>
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                window.dispatchEvent(new CustomEvent('sidebar-action-transmit-selection'));
              })}
            >
              <Send className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Transmettre</span>}
            </Button>
          </>
        );

      case 'subsidy-search':
        return (
          <>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                // Dispatch event for save search
                window.dispatchEvent(new CustomEvent('sidebar-action-save-search'));
              })}
            >
              <BookmarkPlus className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Sauvegarder recherche</span>}
            </Button>

            {/* Saved searches list */}
            {!isCollapsed && savedSearches.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="px-3 mb-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Recherches sauvegardées
                  </span>
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {savedSearches.slice(0, 10).map((search) => (
                    <div
                      key={search.id}
                      className="group flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary rounded-md cursor-pointer"
                      onClick={() => {
                        // Apply the saved search
                        const params = encodeFiltersToParams(
                          search.filters,
                          search.query || '',
                          search.sort_by
                        );
                        setSearchParams(params);
                        recordUse(search.id);
                        onMobileClose?.();
                      }}
                    >
                      <Bookmark className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate text-foreground">{search.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSearch(search.id);
                        }}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isCollapsed && savedSearchesLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        );

      case 'subsidy-detail':
        return (
          <>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                // Dispatch event for add to profile
                window.dispatchEvent(new CustomEvent('sidebar-action-add-to-profile', {
                  detail: { subsidyId }
                }));
              })}
            >
              <PlusCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Ajouter à un profil</span>}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => {
                // Dispatch event for share
                window.dispatchEvent(new CustomEvent('sidebar-action-share-subsidy', {
                  detail: { subsidyId }
                }));
              })}
            >
              <Share2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Partager</span>}
            </Button>
            <Button
              variant="ghost"
              className={buttonClass}
              onClick={() => handleAction(() => navigate('/subventions/recherche'))}
            >
              <ArrowLeft className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {!isCollapsed && <span className="text-left break-words">Retour à la recherche</span>}
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  const actions = renderActions();
  if (!actions) return null;

  return (
    <div className="space-y-1 w-full overflow-hidden">
      {/* Divider */}
      {!isCollapsed && <div className="border-t border-border my-4" />}

      {/* Section title (only when not collapsed) */}
      {!isCollapsed && (
        <div className="px-3 mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Actions rapides
          </span>
        </div>
      )}

      {/* Action buttons */}
      {actions}
    </div>
  );
}
